import { Button, Form, Input, Modal, Space, message } from "antd";
import {
    AUTH_EMTICATION_STATE_MAP,
    AUTH_PERSON_STATE_MAP,
    CERTIFY_TYPE,
    VERIFICATION_AUTH_STATE,
    YES_OR_NO
} from "@zhihaoscm/meta";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BaseItem } from "zhihao-ui-react";
import { certificationService } from "@/api/certificationService";
import { fileService } from "@/api/fileService";
import { institutionApplyService } from "@/api/institutionApplyService";
import { POWER_ATTORNEY_FILE_URL } from "@/api/legacy";
import { ocrService } from "@/api/ocrService";
import { verificationService } from "@/api/verificationService";
import { Breadcrumb } from "@/components/common";
import { ImageUpload, QrCode, type UploadedFile } from "@/components/media";
import { refreshUserInfo } from "@/runtime/bootstrap";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";

interface PersonalForm {
    name: string;
    idNo: string;
}
interface EnterpriseForm {
    businessFile?: UploadedFile;
    powerAttorneyFile?: UploadedFile;
    institutionName: string;
    unifiedSocialCreditCode: string;
    legalRepresentative: string;
    remark?: string;
}
interface InstitutionRecord {
    id?: number;
    state?: number;
    isCancel?: number;
    businessLicenseFileId?: number;
    powerAttorneyFileId?: number;
    institutionName?: string;
    unifiedSocialCreditCode?: string;
    legalRepresentative?: string;
    remark?: string;
}

/** 认证页敏感信息脱敏函数，空值统一回退为 --。 */
export function maskAuthName(name?: string) {
    if (!name) return "--";
    return name.length === 2 ? `${name[0]}*` : `${name.slice(0, -2)}*${name.slice(-1)}`;
}
export function maskAuthId(idNo?: string) {
    return idNo ? `${idNo.slice(0, 3)}***********${idNo.slice(-4)}` : "--";
}

/** 将旧版个人认证、组织认证和人脸轮询状态机集中到一个 React 页面。 */
export function AuthenticationPage() {
    const [personalForm] = Form.useForm<PersonalForm>();
    const [enterpriseForm] = Form.useForm<EnterpriseForm>();
    const [type, setType] = useState<number>(CERTIFY_TYPE.PERSONAL.code);
    const [personState, setPersonState] = useState<number>(AUTH_PERSON_STATE_MAP.PENDING);
    const [enterpriseState, setEnterpriseState] = useState<number>(AUTH_EMTICATION_STATE_MAP.NOT_STATE);
    const [applyState, setApplyState] = useState(0);
    const [institutionState, setInstitutionState] = useState(0);
    const [isCancel, setIsCancel] = useState(0);
    const [lastApplyId, setLastApplyId] = useState(0);
    const [faceAuth, setFaceAuth] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [originalInstitutionName, setOriginalInstitutionName] = useState("");
    const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
    /** 结果页不会挂载 Ant Form，证照必须像旧 Vue 响应式对象一样独立保留。 */
    const enterpriseFilesRef = useRef<{ businessFile?: UploadedFile; powerAttorneyFile?: UploadedFile }>({});
    const token = useUserSession(state => state.zhihaoWebToken);
    const accountType = useAccountSession(state => state.accountType);
    const isSubAccount = accountType === AccountType.SUB_ACCOUNT;

    /** 查询最新组织记录，并还原审核状态及表单快照。 */
    const loadInstitution = async () => {
        const customer = useUserSession.getState().userInfo.customer;
        setApplyState(Number(customer.applyState || 0));
        try {
            const data = (await institutionApplyService.getLatestCertificationRecordV2()) as {
                institutionApply?: InstitutionRecord;
                institutionApplySnapshot?: InstitutionRecord;
            };
            const record = data?.institutionApply;
            if (!record) return undefined;
            setEnterpriseState(record.state ?? AUTH_EMTICATION_STATE_MAP.NOT_STATE);
            setInstitutionState(record.state ?? 0);
            setIsCancel(record.isCancel ?? 0);
            setLastApplyId(record.id ?? 0);
            setType(CERTIFY_TYPE.ENTERPRISE.code);
            setOriginalInstitutionName(record.institutionName ?? "");
            const shown = data.institutionApplySnapshot ?? record;
            enterpriseFilesRef.current = {
                businessFile: shown.businessLicenseFileId ? { id: shown.businessLicenseFileId, path: "" } : undefined,
                powerAttorneyFile: shown.powerAttorneyFileId ? { id: shown.powerAttorneyFileId, path: "" } : undefined
            };
            enterpriseForm.setFieldsValue({
                institutionName: shown.institutionName ?? "",
                unifiedSocialCreditCode: shown.unifiedSocialCreditCode ?? "",
                legalRepresentative: shown.legalRepresentative ?? "",
                remark: record.remark ?? "",
                ...enterpriseFilesRef.current
            });
            return record;
        } catch {
            return undefined;
        }
    };

    /** 初始化用户及个人认证状态，待人脸认证时恢复二维码轮询。 */
    const load = async () => {
        try {
            await refreshUserInfo();
        } catch {
            /* 使用当前会话降级。 */
        }
        const institutionRecord = await loadInstitution();
        try {
            const detail = (await certificationService.getUserAuthDetail()) as any;
            const personal = detail?.personalCertification;
            personalForm.setFieldsValue({ name: personal?.name ?? "", idNo: personal?.idNo ?? "" });
            if (personal?.status === AUTH_PERSON_STATE_MAP.PENDING && personal?.name && personal?.idNo) {
                setPersonState(AUTH_PERSON_STATE_MAP.PENDING);
                setFaceAuth(true);
            } else if (personal?.status === AUTH_PERSON_STATE_MAP.ERROR) {
                setPersonState(AUTH_PERSON_STATE_MAP.PENDING);
                setFaceAuth(false);
            } else setPersonState(personal?.status ?? AUTH_PERSON_STATE_MAP.PENDING);
            // 旧 Vue 在个人实名成功后自动进入组织机构认证；未成功时才停留在个人认证。
            setType(
                personal?.status === AUTH_PERSON_STATE_MAP.SUCCESS
                    ? CERTIFY_TYPE.ENTERPRISE.code
                    : CERTIFY_TYPE.PERSONAL.code
            );
            if (
                detail?.customer?.applyState === YES_OR_NO.NO.code &&
                institutionRecord?.isCancel === YES_OR_NO.YES.code
            ) {
                setType(CERTIFY_TYPE.PERSONAL.code);
                setEnterpriseState(AUTH_EMTICATION_STATE_MAP.NOT_STATE);
            }
        } catch {
            void message.error("认证信息加载失败，请稍后重试");
        }
    };
    useEffect(() => {
        void load();
    }, []);

    /** 人脸认证每 3 秒轮询一次，得到明确结果或异常后立即停止。 */
    useEffect(() => {
        if (!faceAuth) return;
        timerRef.current = setInterval(
            () =>
                void verificationService
                    .verificationFactorAuthResult()
                    .then(result => {
                        if (![0, 1].includes(result.passed)) return;
                        if (timerRef.current) clearInterval(timerRef.current);
                        setFaceAuth(false);
                        if (result.passed === 0) {
                            void message.success("认证成功");
                            void load();
                        } else void message.error("认证失败");
                    })
                    .catch(() => {
                        if (timerRef.current) clearInterval(timerRef.current);
                    }),
            3000
        );
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [faceAuth]);

    const qrUrl = useMemo(() => {
        const values = personalForm.getFieldsValue();
        const base = import.meta.env.VITE_MAP_URL || window.location.origin;
        const query = new URLSearchParams({
            token,
            origin: "1",
            name: (values.name || "").replace(/\s+/g, ""),
            identifyNum: (values.idNo || "").replace(/\s+/g, ""),
            callbackPage: `${base}/certificationDetail`
        });
        return `${base}/certification?${query}`;
    }, [faceAuth, token]);

    /** 提交个人三要素认证，根据业务码进入成功、扫码或重填状态。 */
    const submitPersonal = async () => {
        const values = await personalForm.validateFields();
        await new Promise<void>((resolve, reject) =>
            Modal.confirm({
                title: "是否提交",
                content: "请确保真实姓名、身份证号准确无误",
                okText: "确定",
                cancelText: "取消",
                onOk: () => resolve(),
                onCancel: () => reject(new Error("cancel"))
            })
        );
        setSubmitting(true);
        try {
            const clean = { name: values.name.replace(/\s+/g, ""), identifyNum: values.idNo.replace(/\s+/g, "") };
            const result = await verificationService.verificationThreeFactorAuth(clean);
            if (result.resultBizCode === VERIFICATION_AUTH_STATE.ALL_CONSISTENT.code) {
                setPersonState(AUTH_PERSON_STATE_MAP.SUCCESS);
                void message.success("认证成功");
                void load();
            } else if (result.resultBizCode === VERIFICATION_AUTH_STATE.PHONE_INCONSISTENT.code) setFaceAuth(true);
            else void message.error("身份证号与姓名不一致，请重新输入");
        } finally {
            setSubmitting(false);
        }
    };

    /** 营业执照上传完成后用 OCR 回填企业三项核心字段。 */
    const changeBusinessFile = async (file?: UploadedFile) => {
        enterpriseFilesRef.current.businessFile = file;
        enterpriseForm.setFieldValue("businessFile", file);
        if (!file) return;
        try {
            const data = (await ocrService.businessLicense(file.id)) as {
                companyName?: string;
                creditCode?: string;
                legalPerson?: string;
            };
            enterpriseForm.setFieldsValue({
                institutionName: data.companyName ?? "",
                unifiedSocialCreditCode: data.creditCode ?? "",
                legalRepresentative: data.legalPerson ?? ""
            });
            void message.success("上传成功");
        } catch {
            enterpriseForm.setFieldValue("businessFile", undefined);
            void message.error("营业执照识别失败，请重新上传");
        }
    };

    /** 首次/解除后新增申请，驳回或已通过时提交修改申请。 */
    const submitEnterprise = async () => {
        const values = await enterpriseForm.validateFields();
        await new Promise<void>((resolve, reject) =>
            Modal.confirm({
                title: "提示",
                content: "是否提交，提交后需等待平台审核",
                okText: "确定",
                cancelText: "取消",
                onOk: () => resolve(),
                onCancel: () => reject(new Error("cancel"))
            })
        );
        const params = {
            institutionName: values.institutionName,
            unifiedSocialCreditCode: values.unifiedSocialCreditCode,
            businessLicenseFileId: values.businessFile!.id,
            legalRepresentative: values.legalRepresentative,
            powerAttorneyFileId: values.powerAttorneyFile?.id
        };
        setSubmitting(true);
        try {
            if (
                !applyState &&
                ((institutionState === AUTH_EMTICATION_STATE_MAP.SUCCESS && isCancel === YES_OR_NO.YES.code) ||
                    !lastApplyId)
            )
                await institutionApplyService.add(params);
            else if ([AUTH_EMTICATION_STATE_MAP.ERROR, AUTH_EMTICATION_STATE_MAP.SUCCESS].includes(institutionState))
                await institutionApplyService.edit(lastApplyId, params);
            await loadInstitution();
            void message.success("提交成功");
        } finally {
            setSubmitting(false);
        }
    };

    /** 旧 resetEnterpriseAuth 会恢复两张历史证照的真实地址，不能只保留文件 ID。 */
    const resetEnterpriseAuth = async () => {
        setEnterpriseState(AUTH_EMTICATION_STATE_MAP.NOT_STATE);
        const values = enterpriseFilesRef.current;
        const [businessFile, powerAttorneyFile] = await Promise.all([
            values.businessFile?.id
                ? fileService.getFileById(values.businessFile.id).catch(() => undefined)
                : undefined,
            values.powerAttorneyFile?.id
                ? fileService.getFileById(values.powerAttorneyFile.id).catch(() => undefined)
                : undefined
        ]);
        enterpriseForm.setFieldsValue({
            businessFile: values.businessFile?.id
                ? { id: values.businessFile.id, path: businessFile?.path ?? "" }
                : undefined,
            powerAttorneyFile: values.powerAttorneyFile?.id
                ? { id: values.powerAttorneyFile.id, path: powerAttorneyFile?.path ?? "" }
                : undefined
        });
        enterpriseFilesRef.current = enterpriseForm.getFieldsValue(["businessFile", "powerAttorneyFile"]);
    };

    /** 点击组织认证时保留 Vue 对解除认证记录的清空规则。 */
    const changeCertificationType = (value: number) => {
        if (value !== CERTIFY_TYPE.PERSONAL.code && personState !== AUTH_PERSON_STATE_MAP.SUCCESS) return;
        setType(value);
        if (
            value === CERTIFY_TYPE.ENTERPRISE.code &&
            enterpriseState === AUTH_EMTICATION_STATE_MAP.NOT_STATE &&
            isCancel
        ) {
            enterpriseFilesRef.current = {};
            enterpriseForm.resetFields();
        }
    };

    const personalValues = personalForm.getFieldsValue();
    return (
        <section className="authentication-content">
            <div className="section-card authentication-card">
                <Breadcrumb routes={[{ name: "账号管理" }, { name: "认证" }]} />
                <div className="authentication-title">认证</div>
                <div className="certify-page">
                    <CertificationSteps
                        type={type}
                        personState={personState}
                        enterpriseState={enterpriseState}
                        onChange={changeCertificationType}
                    />
                    {type === CERTIFY_TYPE.PERSONAL.code ? (
                        <section className="authentication-panel">
                            {personState === AUTH_PERSON_STATE_MAP.PENDING && !faceAuth ? (
                                <Form form={personalForm} layout="vertical" className="authentication-form">
                                    <Form.Item
                                        name="name"
                                        label="真实姓名"
                                        rules={[{ required: true, message: "请输入您的真实姓名" }]}
                                    >
                                        <Input maxLength={25} placeholder="请输入真实姓名" />
                                    </Form.Item>
                                    <Form.Item
                                        name="idNo"
                                        label="身份证号"
                                        className="mb-0"
                                        rules={[
                                            { required: true, message: "请输入您的身份证号码" },
                                            {
                                                pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
                                                message: "请输入长度15或18个字符的正确的身份证号"
                                            }
                                        ]}
                                    >
                                        <Input placeholder="请输入身份证号" />
                                    </Form.Item>
                                    <div className="auth-info-card">
                                        <i className="iconfont icon-Frame" />
                                        <span>信息仅用于身份验证</span>
                                    </div>
                                    <div className="auth-submit">
                                        <Button
                                            type="primary"
                                            loading={submitting}
                                            onClick={() => void submitPersonal()}
                                        >
                                            提交
                                        </Button>
                                    </div>
                                </Form>
                            ) : faceAuth ? (
                                <div className="auth-qrcode">
                                    <div className="auth-qrcode-box">
                                        <QrCode value={qrUrl} size={210} />
                                    </div>
                                    <div className="auth-qrcode-desc">请用手机扫描下方二维码继续完成身份认证</div>
                                    <div className="auth-fields">
                                        <BaseItem label="真实姓名" value={maskAuthName(personalValues.name)} />
                                        <BaseItem label="身份证号" value={maskAuthId(personalValues.idNo)} />
                                    </div>
                                    <Button onClick={() => setFaceAuth(false)}>取消</Button>
                                </div>
                            ) : (
                                <AuthResult
                                    image="auth-success.png"
                                    title="您已完成个人认证"
                                    fields={[
                                        { label: "真实姓名", value: maskAuthName(personalValues.name) },
                                        { label: "身份证号", value: maskAuthId(personalValues.idNo) }
                                    ]}
                                    action={
                                        !isSubAccount && enterpriseState === AUTH_EMTICATION_STATE_MAP.NOT_STATE ? (
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    enterpriseForm.resetFields();
                                                    setType(CERTIFY_TYPE.ENTERPRISE.code);
                                                }}
                                            >
                                                申请组织机构认证
                                            </Button>
                                        ) : null
                                    }
                                />
                            )}
                        </section>
                    ) : (
                        <section className="authentication-panel">
                            {enterpriseState === AUTH_EMTICATION_STATE_MAP.NOT_STATE ? (
                                <Form
                                    form={enterpriseForm}
                                    layout="vertical"
                                    className="authentication-form enterprise-auth-form"
                                >
                                    <div className="enterprise-upload-grid">
                                        <Form.Item
                                            name="businessFile"
                                            label="营业执照"
                                            rules={[{ required: true, message: "请上传营业执照" }]}
                                        >
                                            <ImageUpload
                                                value={enterpriseForm.getFieldValue("businessFile")}
                                                accept={["image/jpeg", "image/png"]}
                                                maxSizeMb={10}
                                                width={235}
                                                height={123}
                                                emptyIconClass="icon-Frame-3"
                                                emptyLabel="上传"
                                                onChange={file => void changeBusinessFile(file)}
                                            />
                                            <div className="upload-img-tip">
                                                请上传最新版三合一的营业执照清晰正面图，支持上传jpg文件和png文件，最大不超过10M
                                            </div>
                                        </Form.Item>
                                        <Form.Item
                                            name="powerAttorneyFile"
                                            label={
                                                <Space>
                                                    委托授权书
                                                    <a
                                                        className="template-download-btn"
                                                        href={POWER_ATTORNEY_FILE_URL}
                                                        download
                                                    >
                                                        下载模板
                                                    </a>
                                                </Space>
                                            }
                                        >
                                            <ImageUpload
                                                value={enterpriseForm.getFieldValue("powerAttorneyFile")}
                                                accept={["image/jpeg", "image/png"]}
                                                maxSizeMb={10}
                                                width={235}
                                                height={123}
                                                emptyIconClass="icon-Frame-3"
                                                emptyLabel="上传"
                                                onChange={file => {
                                                    enterpriseFilesRef.current.powerAttorneyFile = file;
                                                    enterpriseForm.setFieldValue("powerAttorneyFile", file);
                                                }}
                                            />
                                            <div className="upload-img-tip">最大不超过10M</div>
                                        </Form.Item>
                                    </div>
                                    <Form.Item
                                        name="institutionName"
                                        label="企业名称"
                                        rules={[{ required: true, message: "请输入企业名称" }]}
                                    >
                                        <Input maxLength={40} placeholder="请输入企业全称" />
                                    </Form.Item>
                                    <Form.Item
                                        name="unifiedSocialCreditCode"
                                        label="统一社会信用代码"
                                        rules={[{ required: true, message: "请输入统一社会信用代码" }]}
                                    >
                                        <Input maxLength={18} placeholder="请输入统一社会信用代码" />
                                    </Form.Item>
                                    <Form.Item
                                        name="legalRepresentative"
                                        label="法定代表人"
                                        rules={[{ required: true, message: "请输入法定代表人名称" }]}
                                    >
                                        <Input maxLength={10} placeholder="请输入法定代表人名称" />
                                    </Form.Item>
                                    <div className="enterprise-card">
                                        <div>
                                            <i className="iconfont icon-Frame" />
                                            <span>提示</span>
                                        </div>
                                        <div>1.您的营业执照和委托授权书将仅用于证明企业的身份和合法经营资格。</div>
                                        <div>
                                            2.本平台对您提交的营业执照信息保密，不会将您的营业执照用于任何未经授权的商业活动或其他用途。
                                        </div>
                                    </div>
                                    <div className="auth-submit">
                                        <Button
                                            type="primary"
                                            loading={submitting}
                                            onClick={() => void submitEnterprise()}
                                        >
                                            提交
                                        </Button>
                                    </div>
                                </Form>
                            ) : (
                                <AuthResult
                                    image={
                                        enterpriseState === AUTH_EMTICATION_STATE_MAP.PENDING
                                            ? "auth-pending.png"
                                            : enterpriseState === AUTH_EMTICATION_STATE_MAP.ERROR
                                              ? "auth-failed.png"
                                              : "auth-success.png"
                                    }
                                    title={
                                        enterpriseState === AUTH_EMTICATION_STATE_MAP.PENDING
                                            ? "组织机构认证等待审核中"
                                            : enterpriseState === AUTH_EMTICATION_STATE_MAP.ERROR
                                              ? "您的组织机构认证已被驳回"
                                              : "您已通过组织机构认证"
                                    }
                                    fields={[
                                        {
                                            label: "企业名称",
                                            value: enterpriseForm.getFieldValue("institutionName") || "--"
                                        },
                                        ...(enterpriseState === AUTH_EMTICATION_STATE_MAP.ERROR
                                            ? [
                                                  {
                                                      label: "驳回理由",
                                                      value: enterpriseForm.getFieldValue("remark") || "--"
                                                  }
                                              ]
                                            : [])
                                    ]}
                                    originalInstitutionName={
                                        originalInstitutionName &&
                                        originalInstitutionName !== enterpriseForm.getFieldValue("institutionName")
                                            ? originalInstitutionName
                                            : undefined
                                    }
                                    action={
                                        !isSubAccount && enterpriseState !== AUTH_EMTICATION_STATE_MAP.PENDING ? (
                                            <Button type="primary" onClick={() => void resetEnterpriseAuth()}>
                                                {enterpriseState === AUTH_EMTICATION_STATE_MAP.ERROR
                                                    ? "重新申请组织机构认证"
                                                    : "申请修改组织机构认证"}
                                            </Button>
                                        ) : null
                                    }
                                />
                            )}
                        </section>
                    )}
                </div>
            </div>
        </section>
    );
}

export function CertificationSteps({
    type,
    personState,
    enterpriseState,
    onChange,
    completeIcon = "icon-buzoufinish",
    completeOnlyWhenInactive = false
}: {
    type: number;
    personState: number;
    enterpriseState: number;
    onChange: (value: number) => void;
    completeIcon?: string;
    completeOnlyWhenInactive?: boolean;
}) {
    const personDone = personState === AUTH_PERSON_STATE_MAP.SUCCESS;
    const enterpriseDone = enterpriseState === AUTH_EMTICATION_STATE_MAP.SUCCESS;
    const showPersonComplete = personDone && (!completeOnlyWhenInactive || type !== CERTIFY_TYPE.PERSONAL.code);
    const showEnterpriseComplete =
        enterpriseDone && (!completeOnlyWhenInactive || type !== CERTIFY_TYPE.ENTERPRISE.code);
    return (
        <div className="certification-steps">
            <button type="button" onClick={() => onChange(CERTIFY_TYPE.PERSONAL.code)}>
                {showPersonComplete ? (
                    <i className={`iconfont ${completeIcon}${type !== CERTIFY_TYPE.PERSONAL.code ? " success" : ""}`} />
                ) : (
                    <i className={type === CERTIFY_TYPE.PERSONAL.code ? "is-active" : ""}>1</i>
                )}
                <span className={type === CERTIFY_TYPE.PERSONAL.code || personDone ? "is-active" : ""}>
                    个人实名认证
                </span>
            </button>
            <div className={personDone ? "is-active" : ""} />
            <button type="button" onClick={() => onChange(CERTIFY_TYPE.ENTERPRISE.code)}>
                {showEnterpriseComplete ? (
                    <i
                        className={`iconfont ${completeIcon}${type !== CERTIFY_TYPE.ENTERPRISE.code ? " success" : ""}`}
                    />
                ) : (
                    <i
                        className={`${type === CERTIFY_TYPE.ENTERPRISE.code ? "is-active " : ""}${!personDone ? "is-disabled" : ""}`}
                    >
                        2
                    </i>
                )}
                <span className={type === CERTIFY_TYPE.ENTERPRISE.code ? "is-active" : ""}>组织机构认证</span>
            </button>
        </div>
    );
}
export function AuthFields({ fields }: { fields: Array<{ label: string; value: string }> }) {
    return (
        <div className="auth-fields">
            {fields.map(field => (
                <div className="auth-field" key={field.label}>
                    <span>{field.label}</span>
                    <strong>{field.value}</strong>
                </div>
            ))}
        </div>
    );
}
export function AuthResult({
    image,
    title,
    fields,
    originalInstitutionName,
    action
}: {
    image: string;
    title: string;
    fields: Array<{ label: string; value: string }>;
    originalInstitutionName?: string;
    action?: ReactNode;
}) {
    return (
        <div className="auth-result">
            <img src={`https://static.zhihaoscm.cn/customer-admin/${image}`} alt="认证状态" />
            <div className="auth-result-title">{title}</div>
            <AuthFields fields={fields} />
            {originalInstitutionName ? (
                <div className="auth-original-info">
                    <div className="auth-original-info__title">原认证信息</div>
                    <AuthFields fields={[{ label: "企业名称", value: originalInstitutionName }]} />
                </div>
            ) : null}
            {action}
        </div>
    );
}
